import React, { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form-7";

import useNotification from "../../hooks/useNotification";
import useTranslations from "../../hooks/useTranslations";
import {
  InsertProfessionInput,
  ProfessionFragment,
  useDeleteProfession,
  useInsertProfession,
} from "../../graphql/generated/definitions";

import useConfirm from "../utils/confirm/useConfirm";
import { Input } from "../wombat-ui";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import useViewer from "../../hooks/useViewer";

interface EditProfessionDialogProps {
  open: boolean;
  profession: ProfessionFragment;
  onClose: () => void;
  onInsert: (data: InsertProfessionInput) => void;
  onDelete: () => void;
}

const EditProfessionDialog: React.FC<EditProfessionDialogProps> = ({
  open,
  profession,
  onClose,
  onInsert,
  onDelete,
}) => {
  const t = useTranslations();
  const confirm = useConfirm();
  const { notifySuccess, notifyError } = useNotification();

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<{ id: string; name: string }>({});

  const { companyId } = useViewer();
  // reset the form whenever the open value changes
  // to avoid confusion with older values
  useEffect(reset, [open]);

  const [insertProfession] = useInsertProfession();

  const onSubmit = useCallback(
    async (data) => {
      try {
        const result = await insertProfession({
          variables: {
            input: {
              ...data,
              companyId
            },
          },
        });
        notifySuccess({
          title: t(
            `professionSkillLibrary.notification.profession.${
              profession ? "update" : "create"
            }.success`,
            { name: data.name }
          ) as string,
        });
        onInsert(result.data.insertProfession.profession);
      } catch {
        notifyError({
          title: t(
            `professionSkillLibrary.notification.profession.${
              profession ? "update" : "create"
            }.error`
          ) as string,
        });
      }
    },
    [profession, onInsert, insertProfession]
  );

  const [deleteProfession] = useDeleteProfession();

  const handleDelete = useCallback(() => {
    confirm({
      title: t(
        `professionSkillLibrary.dialog.profession.destroyConfirmTitle`,
        {
          name: profession.name,
        }
      ),
      message: t(`professionSkillLibrary.dialog.profession.destroyConfirm`),
      buttons: [
        { text: t("forms.actions.cancel") },
        {
          colorScheme: "red",
          id: "delete-profession",
          text: t("forms.actions.delete"),
          onClick: async () => {
            try {
              await deleteProfession({
                variables: {
                  input: { id: profession.id },
                },
              });
              notifySuccess({
                title: t(
                  `professionSkillLibrary.notification.profession.destroy.success`,
                  { name: profession.name }
                ) as string,
              });
              onDelete();
            } catch (e) {
              notifyError({
                title: t(
                  `professionSkillLibrary.notification.profession.destroy.error`
                ) as string,
              });
            }
          },
        },
      ],
    });
  }, [onDelete, deleteProfession, profession]);

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form id="professionForm" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            {profession
              ? t("professionSkillLibrary.dialog.profession.title.update", {
                  name: profession.name,
                })
              : t("professionSkillLibrary.dialog.profession.title.create")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <input
              type="hidden"
              {...register("id")}
              id="professionId"
              defaultValue={profession?.id}
            />
            <Input
              {...register("name", { required: true })}
              label={t("professionSkillLibrary.nameLabel") as string}
              defaultValue={profession?.name}
              id="professionName"
            />
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button onClick={onClose} id="cancelEditProfessionButton">
                {t("forms.actions.cancel")}
              </Button>
              {profession?.canDestroy && (
                <>
                  <Spacer />
                  <Button
                    variant="outline"
                    onClick={handleDelete}
                    colorScheme="red"
                    id="deleteProfessionButton"
                  >
                    {t("forms.actions.delete")}
                  </Button>
                  <Spacer />
                </>
              )}
              <Button
                type="submit"
                disabled={!!errors.name}
                id="submitEditProfessionButton"
                colorScheme="blue"
              >
                {t("forms.actions.save")}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default memo(EditProfessionDialog);
